import { useEffect, useRef } from 'react';

/**
 * Alt+F2 — "Change Period", the Tally shortcut. Opens the report's date filter
 * and drops the cursor straight into the From field, so a period can be changed
 * without reaching for the mouse.
 *
 * Registration-based rather than one listener per page: the hook binds a single
 * window listener for the whole app the first time any page opts in, and each
 * page contributes only its own "open the filter" callback. Adding it to
 * another report is one line.
 *
 * Deliberately NOT suppressed while typing. Plain keys like Backspace have to
 * stand down inside a text field or they fight the input, but Alt+F2 is a chord
 * no field consumes — and in Tally it works from anywhere, including mid-entry.
 *
 *     useChangePeriodKey(() => setShowControls(true));
 */

type Handler = () => void;

// Only one report is mounted at a time in practice, but a stack keeps this
// sane if one ever layers over another: the most recently mounted page wins.
const stack: Handler[] = [];
let bound = false;

function onKey(e: KeyboardEvent) {
  // Alt+F2 only — Ctrl/Meta variants belong to the OS or the browser.
  if (e.key !== 'F2' || !e.altKey || e.ctrlKey || e.metaKey) return;
  const handler = stack[stack.length - 1];
  if (!handler) return;
  e.preventDefault();
  handler();
}

function focusFirstDateInput() {
  // Wait a frame: the filter panel is usually mounted by the state change that
  // just happened, so it isn't in the DOM yet when the handler returns.
  requestAnimationFrame(() => {
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[type="date"]'));
    // offsetParent is null for anything still display:none, which skips the
    // responsive twin that is hidden at the current breakpoint — otherwise we
    // would focus an invisible field and the panel would look unresponsive.
    const target = inputs.find(el => el.offsetParent !== null && !el.disabled && !el.readOnly);
    if (!target) return;
    target.focus();
    // Pop the native calendar where the browser allows it. showPicker() needs a
    // user activation, which the keypress provides; it throws where it doesn't
    // apply, and focus alone is already a usable result.
    try { target.showPicker?.(); } catch { /* focus is enough */ }
  });
}

export default function useChangePeriodKey(open: Handler, enabled = true) {
  // Hold the callback in a ref so the listener registration doesn't churn on
  // every render — the page passes a fresh arrow function each time.
  const ref = useRef(open);
  ref.current = open;

  useEffect(() => {
    if (!enabled) return;
    const entry = () => { ref.current(); focusFirstDateInput(); };
    stack.push(entry);
    if (!bound) {
      window.addEventListener('keydown', onKey);
      bound = true;
    }
    return () => {
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [enabled]);
}
